import { action, observable } from 'mobx'
import { mustBeInRange } from '@/utils/validx-util'
import { validationContext, required } from 'validx'
import { type InventoryItemPricingEditor } from './inventory-item-pricing-editor'
import {
  type InventoryItemMarkupValues,
  type InventoryItemPricingValues,
} from '@/features/inventory/inventory-item-pricing-values'

/**
 * View Model for Inventory Items of type Markup.
 */
export class MarkupEditorViewModel implements InventoryItemPricingEditor {
  /**
   * The rate by which the job's price will be marked up.
   */
  @observable accessor clientMarkupRate = 0

  /**
   * The rate by which the pro's payout will be marked up.
   */
  @observable accessor proPayoutMarkupRate = 0

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    clientMarkupRate: [
      required('A client markup rate is required.'),
      mustBeInRange(0, 100, 'The rate must be between 0 and 100.'),
    ],

    proPayoutMarkupRate: [
      required('A pro payout markup rate is required.'),
      mustBeInRange(0, 100, 'The rate must be between 0 and 100.'),
    ],
  })

  /**
   * @inheritdoc
   */
  validate(): boolean {
    return this.validation.reset().validate().isValid
  }

  /**
   * @inheritdoc
   */
  @action.bound
  hydrate(values: InventoryItemMarkupValues): void {
    this.clientMarkupRate = values.clientMarkupRate
    this.proPayoutMarkupRate = values.proPayoutMarkupRate
  }

  /**
   * @inheritdoc
   */
  toValues(): InventoryItemPricingValues {
    return {
      type: 'Markup',
      clientMarkupRate: this.clientMarkupRate,
      proPayoutMarkupRate: this.proPayoutMarkupRate,
    }
  }

  /**
   * @inheritdoc
   */
  @action.bound
  reset() {
    this.clientMarkupRate = 0
    this.proPayoutMarkupRate = 0
    this.validation.reset()
  }
}
